import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2 } from 'lucide-react';
import { formatIls, giftAdmin } from '@/lib/giftCards';
import { newIdempotencyKey, validateDebit } from '@/lib/giftValidation';

interface Props {
  cardId: string;
  balance: number | null;
  status: string;
  currency: string;
  onDone: () => void | Promise<void>;
}

/**
 * Records service usage against a gift card. The balance itself is never
 * editable here — Shopify performs the debit and returns the new balance.
 */
export function GiftDebitForm({ cardId, balance, status, currency, onDone }: Props) {
  const [amount, setAmount] = useState('');
  const [service, setService] = useState('');
  const [note, setNote] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [idemKey, setIdemKey] = useState(() => newIdempotencyKey('debit'));

  const check = useMemo(
    () => validateDebit({ amount, balance, cardStatus: status }),
    [amount, balance, status],
  );

  const submit = async () => {
    if (!check.ok || busy) return;
    setBusy(true);
    setError(null);
    try {
      await giftAdmin('transaction.debit', {
        gift_card_id: cardId,
        amount: Number(amount),
        currency,
        idempotency_key: idemKey,
        service_reference: service.trim() || null,
        note: note.trim() || null,
      });
      setAmount('');
      setService('');
      setNote('');
      setConfirming(false);
      setIdemKey(newIdempotencyKey('debit'));
      await onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'החיוב נכשל');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3 rounded-lg border p-4">
      <div>
        <h4 className="font-medium">רישום שימוש בשירות</h4>
        <p className="text-xs text-muted-foreground">
          הפעולה מתעדת ניצול של השובר בקליניקה. עדכון היתרה מתבצע בשופיפיי.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="debit-amount">סכום לחיוב (₪)</Label>
          <Input
            id="debit-amount"
            type="number"
            min="0"
            step="1"
            value={amount}
            onChange={(e) => {
              setAmount(e.target.value);
              setConfirming(false);
            }}
            disabled={busy}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="debit-service">שירות</Label>
          <Input
            id="debit-service"
            value={service}
            onChange={(e) => setService(e.target.value)}
            placeholder="לדוגמה: טיפול פנים"
            disabled={busy}
          />
        </div>
      </div>

      <div className="space-y-1">
        <Label htmlFor="debit-note">הערה</Label>
        <Textarea
          id="debit-note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={2}
          disabled={busy}
        />
      </div>

      {amount && !check.ok && (
        <p className="text-sm text-destructive">{check.error}</p>
      )}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {confirming && check.ok ? (
        <div className="space-y-2 rounded-md bg-muted p-3 text-sm">
          <p>
            יתרה לפני: <strong>{formatIls(balance)}</strong> · יתרה אחרי:{' '}
            <strong>{formatIls(check.balanceAfter)}</strong>
          </p>
          <div className="flex gap-2">
            <Button size="sm" onClick={submit} disabled={busy}>
              {busy && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
              אישור החיוב
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setConfirming(false)} disabled={busy}>
              ביטול
            </Button>
          </div>
        </div>
      ) : (
        <Button size="sm" onClick={() => setConfirming(true)} disabled={!check.ok || busy}>
          המשך לאישור
        </Button>
      )}
    </div>
  );
}
